import { Graphics, Texture } from 'pixi.js';
import { assetLoader } from '../core/AssetLoader';

/**
 * Projectile texture colors and configurations.
 */
interface ProjectileConfig {
  laserColor: number;
  laserGlowColor: number;
  kineticColor: number;
  missileColor: number;
}

const PLAYER_CONFIG: ProjectileConfig = {
  laserColor: 0x20a5a6,      // Teal
  laserGlowColor: 0x66cccc,  // Light teal
  kineticColor: 0xf5e8d1,    // Cream
  missileColor: 0xdd5639,    // Orange-red
};

const BOT_CONFIG: ProjectileConfig = {
  laserColor: 0xff4400,      // Red-orange
  laserGlowColor: 0xff6622,  // Light red-orange
  kineticColor: 0xffaa00,    // Orange
  missileColor: 0xff4400,    // Red-orange
};

/**
 * Factory for creating and caching projectile textures.
 * Consolidates duplicate texture generation from Projectile.ts and BotProjectile.ts.
 */
export class ProjectileTextureFactory {
  private static playerTextures: Map<string, Texture> = new Map();
  private static botTextures: Map<string, Texture> = new Map();
  private static playerTexturesGenerated = false;
  private static botTexturesGenerated = false;

  /**
   * Generate all player projectile textures.
   */
  static ensurePlayerTexturesExist(): void {
    if (this.playerTexturesGenerated) return;
    this.generateTextures(this.playerTextures, PLAYER_CONFIG, 'projectile');
    this.playerTexturesGenerated = true;
  }

  /**
   * Generate all bot projectile textures.
   */
  static ensureBotTexturesExist(): void {
    if (this.botTexturesGenerated) return;
    this.generateTextures(this.botTextures, BOT_CONFIG, 'bot-projectile');
    this.botTexturesGenerated = true;
  }

  /**
   * Get a player projectile texture by weapon type.
   */
  static getPlayerTexture(weaponType: string): Texture {
    this.ensurePlayerTexturesExist();
    return this.playerTextures.get(`projectile-${weaponType}`) || this.playerTextures.get('projectile-laser')!;
  }

  /**
   * Get a bot projectile texture by weapon type.
   */
  static getBotTexture(weaponType: string): Texture {
    this.ensureBotTexturesExist();
    return this.botTextures.get(`bot-projectile-${weaponType}`) || this.botTextures.get('bot-projectile-laser')!;
  }

  /**
   * Generate all projectile textures for a given config.
   */
  private static generateTextures(
    textureMap: Map<string, Texture>,
    config: ProjectileConfig,
    prefix: string
  ): void {
    // Laser projectile
    const laserGraphics = new Graphics();
    laserGraphics.moveTo(0, 4);
    laserGraphics.lineTo(8, 4);
    laserGraphics.stroke({ width: 2, color: config.laserColor, alpha: 1 });
    laserGraphics.moveTo(0, 4);
    laserGraphics.lineTo(8, 4);
    laserGraphics.stroke({ width: 3, color: config.laserGlowColor, alpha: 0.3 });
    const laserTexture = assetLoader.generateTexture(laserGraphics, `${prefix}-laser`);
    textureMap.set(`${prefix}-laser`, laserTexture);
    laserGraphics.destroy();

    // Kinetic projectile
    const kineticGraphics = new Graphics();
    kineticGraphics.moveTo(0, 4);
    kineticGraphics.lineTo(10, 4);
    kineticGraphics.stroke({ width: 2, color: config.kineticColor, alpha: 1 });
    const kineticTexture = assetLoader.generateTexture(kineticGraphics, `${prefix}-kinetic`);
    textureMap.set(`${prefix}-kinetic`, kineticTexture);
    kineticGraphics.destroy();

    // Missile projectile (with exhaust trail)
    const missileGraphics = new Graphics();
    missileGraphics.moveTo(2, 4);
    missileGraphics.lineTo(14, 4);
    missileGraphics.stroke({ width: 3, color: config.missileColor, alpha: 1 });
    missileGraphics.moveTo(0, 4);
    missileGraphics.lineTo(2, 4);
    missileGraphics.stroke({ width: 2, color: 0xffffff, alpha: 0.6 });
    const missileTexture = assetLoader.generateTexture(missileGraphics, `${prefix}-missile`);
    textureMap.set(`${prefix}-missile`, missileTexture);
    missileGraphics.destroy();
  }
}

// Re-export the old function names for backwards compatibility
export function ensureProjectileTexturesExist(): void {
  ProjectileTextureFactory.ensurePlayerTexturesExist();
}

export function getProjectileTexture(weaponType: string): Texture {
  return ProjectileTextureFactory.getPlayerTexture(weaponType);
}

export function ensureBotProjectileTexturesExist(): void {
  ProjectileTextureFactory.ensureBotTexturesExist();
}

export function getBotProjectileTexture(weaponType: string): Texture {
  return ProjectileTextureFactory.getBotTexture(weaponType);
}
